import { z } from 'zod'

// ============================================================================
// DEPARTMENT SCHEMAS
// ============================================================================

export const departmentSchema = z.object({
  name: z
    .string()
    .min(2, 'Nome deve ter no mínimo 2 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres')
    .regex(/^[a-zA-ZÀ-ÿ0-9\s-]+$/, 'Nome deve conter apenas letras, números, espaços e hífens'),

  description: z
    .string()
    .max(500, 'Descrição deve ter no máximo 500 caracteres')
    .optional()
    .or(z.literal('')),

  costCenter: z
    .string()
    .regex(/^[a-zA-Z0-9]*$/, 'Centro de custo deve ser alfanumérico')
    .min(5, 'Centro de custo deve ter no mínimo 5 caracteres')
    .max(20, 'Centro de custo deve ter no máximo 20 caracteres')
    .optional()
    .or(z.literal('')),

  budget: z
    .number()
    .min(0, 'Orçamento deve ser positivo')
    .max(1000000000, 'Orçamento deve ser menor que R$ 1.000.000.000')
    .optional(),

  managerId: z.string().optional().or(z.literal('')),

  parentDepartmentId: z.string().optional().or(z.literal('')),
})

export type DepartmentFormData = z.infer<typeof departmentSchema>

// ============================================================================
// ROLE SCHEMAS
// ============================================================================

export const roleLevelSchema = z.enum([
  'junior',
  'mid',
  'senior',
  'lead',
  'manager',
  'director',
  'executive',
])

export const salaryRangeSchema = z.object({
  min: z.number().min(0, 'Salário mínimo deve ser maior que zero'),
  max: z.number().min(0, 'Salário máximo deve ser maior que zero'),
  currency: z.string().default('BRL'),
}).refine(
  (data) => data.max > data.min,
  {
    message: 'Salário máximo deve ser maior que o salário mínimo',
    path: ['max'],
  }
)

export const roleSchema = z.object({
  title: z
    .string()
    .min(2, 'Título deve ter no mínimo 2 caracteres')
    .max(100, 'Título deve ter no máximo 100 caracteres'),

  description: z
    .string()
    .max(1000, 'Descrição deve ter no máximo 1000 caracteres')
    .optional()
    .or(z.literal('')),

  level: roleLevelSchema,

  departmentId: z
    .string()
    .optional()
    .or(z.literal('')),

  salaryRange: salaryRangeSchema.optional(),

  responsibilities: z.array(z.string()).optional(),

  requirements: z.array(z.string()).optional(),
})

// API-compatible role schema (uses 'name' instead of 'title', 'salary' instead of 'salaryRange')
export const roleAPISchema = z.object({
  name: z
    .string()
    .min(2, 'Nome deve ter no mínimo 2 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres'),

  description: z
    .string()
    .max(1000, 'Descrição deve ter no máximo 1000 caracteres')
    .optional()
    .or(z.literal('')),

  level: roleLevelSchema,

  departmentId: z
    .string()
    .optional()
    .or(z.literal('')),

  salary: salaryRangeSchema.optional(),

  responsibilities: z.array(z.string()).optional(),

  requirements: z.array(z.string()).optional(),
})

export type RoleFormData = z.infer<typeof roleSchema>

// ============================================================================
// EMPLOYEE SCHEMAS
// ============================================================================

// Brazilian states
export const BRAZILIAN_STATES: Array<{ value: string; label: string }> = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
]

export const addressSchema = z
  .object({
    street: z.string().optional(),
    city: z.string().optional(),
    state: z.string().length(2, 'Selecione um estado válido').optional().or(z.literal('')),
    zipCode: z
      .string()
      .regex(/^\d{5}-?\d{3}$/, 'CEP inválido (use formato 00000-000)')
      .optional()
      .or(z.literal('')),
  })
  .optional()

export const emergencyContactSchema = z
  .object({
    name: z.string().optional(),
    relationship: z.string().optional(),
    phone: z
      .union([
        z.literal(''),
        z.string().regex(/^\(\d{2}\)\s\d{5}-\d{4}$/, 'Telefone inválido (use formato (11) 98765-4321 com 11 dígitos)')
      ])
      .optional(),
  })
  .optional()

export const benefitsSchema = z.object({
  healthInsurance: z.boolean().default(false),
  dentalInsurance: z.boolean().default(false),
  visionInsurance: z.boolean().default(false),
  lifeInsurance: z.boolean().default(false),
  retirement401k: z.boolean().default(false),
  paidTimeOff: z.number().min(0).max(365).default(20),
  mealVoucher: z.number().min(0).optional(),
  transportVoucher: z.number().min(0).optional(),
  gymMembership: z.boolean().default(false),
})

export const employeeSchema = z.object({
  firstName: z
    .string({ message: 'Nome é obrigatório' })
    .min(2, 'Nome deve ter no mínimo 2 caracteres')
    .max(50, 'Nome deve ter no máximo 50 caracteres'),

  lastName: z
    .string({ message: 'Sobrenome é obrigatório' })
    .min(2, 'Sobrenome deve ter no mínimo 2 caracteres')
    .max(50, 'Sobrenome deve ter no máximo 50 caracteres'),

  email: z
    .string({ message: 'Email é obrigatório' })
    .email('Email inválido')
    .max(100, 'Email deve ter no máximo 100 caracteres'),

  phone: z
    .union([
      z.literal(''),
      z.string().regex(/^\(\d{2}\)\s\d{5}-\d{4}$/, 'Telefone inválido (use formato (11) 98765-4321 com 11 dígitos)') //11987654321
    ])
    .optional(),

  cpf: z
    .string()
    .regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'CPF inválido (use formato 000.000.000-00)')
    .optional()
    .or(z.literal('')),

  departmentId: z
    .string({ message: 'Departamento é obrigatório' })
    .min(1, 'Departamento é obrigatório'),

  roleId: z
    .string({ message: 'Cargo é obrigatório' })
    .min(1, 'Cargo é obrigatório'),

  hireDate: z.coerce.date({
    message: 'Data de admissão é obrigatória',
  }),

  salary: z
    .number({ message: 'Salário é obrigatório' })
    .min(0, 'Salário deve ser maior ou igual a zero')
    .max(10000000, 'Salário deve ser menor que R$ 10.000.000'),

  address: addressSchema.optional(),

  emergencyContact: emergencyContactSchema.optional(),

  benefits: benefitsSchema.optional(),
})

export type EmployeeFormData = z.infer<typeof employeeSchema>

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Transforms empty strings to undefined for optional fields
 */
export function cleanFormData<T extends Record<string, any>>(data: T): T {
  const cleaned = { ...data } as Record<string, any>

  Object.keys(cleaned).forEach((key) => {
    const value = cleaned[key]

    if (value === '') {
      cleaned[key] = undefined
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      cleaned[key] = cleanFormData(value)
    }
  })

  return cleaned as T
}

/**
 * Format CPF with mask
 */
export function formatCPF(value: string): string {
  const numbers = value.replace(/\D/g, '')
  if (numbers.length <= 11) {
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }
  return value
}

/**
 * Format phone with mask - Brazilian format with space after DDD
 * ONLY accepts 11 digits (mobile format)
 */
export function formatPhone(value: string): string {
  const numbers = value.replace(/\D/g, '').slice(0, 11) // Limit to 11 digits

  if (numbers.length === 0) return ''
  if (numbers.length <= 2) return `(${numbers}`
  if (numbers.length < 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`

  // Add hyphen only when we have 7+ digits (will complete at 11)
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`
}

/**
 * Format CEP with mask
 */
export function formatCEP(value: string): string {
  const numbers = value.replace(/\D/g, '')
  return numbers.replace(/(\d{5})(\d{3})/, '$1-$2')
}
